import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Tabs } from '@creditornot/cb-tabs';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { AssetSource, Image } from 'apis/asset-repository-service/types';
import { useAllOfferImages } from 'modules/shared-menu/hooks/useAllOfferImages';
import { UploadResult } from 'modules/shared-menu/hooks/useAssetRepositoryUpload/useAssetRepositoryUpload';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';

import { ImageCardSkeleton } from './ImageCardSkeleton';
import { ImageGalleryHeader } from './ImageGalleryHeader';
import { ListingManagerErrorPage } from '../../../components/ListingManagerErrorPage';
import { CenteredEmpty } from '../components/CenteredEmpty';
import { ImageCard } from '../components/ImageCard';
import { ImageGridLoader } from '../components/ImageGridLoader';
import { ImagesUploader } from '../components/ImagesUploader/ImagesUploader';
import { IMAGE_BASE_URL, PAGE_SIZE } from '../constants';
import {
  useListingManagerImageGallery,
  useUpdateListingManagerImageGallery,
} from '../hooks/useListingManagerImageGallery';

type OptionTab = keyof typeof optionTabs;

const optionTabs = {
  ALL_IMAGES: 'ALL_IMAGES',
  PHOTOSHOOT_IMAGES: 'PHOTOSHOOT_IMAGES',
} as const;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const TabsHeader = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const TabsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const TabsContentTopBar = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Search = styled.div`
  width: 320px;
`;

const TAB_TO_SOURCE_MAP: Record<OptionTab, AssetSource['type'] | undefined> = {
  [optionTabs.ALL_IMAGES]: undefined,
  [optionTabs.PHOTOSHOOT_IMAGES]: 'photographer',
};

type Props = {
  hasAddToOfferAction?: boolean;
  onImageCardClick: (asset: Image) => void;
  selectedImagesIds: string[];
  headerContent?: React.ReactNode;
  onHeaderExitClick?: () => void;
};

export const ImageGalleryBrowseImages = ({
  hasAddToOfferAction,
  onImageCardClick,
  selectedImagesIds,
  headerContent,
  onHeaderExitClick,
}: Props) => {
  const imageGridRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();
  const history = useHistory();
  const [activeTabId, setActiveTabId] = useState<OptionTab>(optionTabs.ALL_IMAGES);
  const [newLoadingImagesCount, setNewLoadingImagesCount] = useState<number>(0);
  const { imageAssets, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, isSuccess, error } =
    useListingManagerImageGallery({
      size: PAGE_SIZE,
      source: TAB_TO_SOURCE_MAP[activeTabId],
    });
  const { allOfferImagesSet } = useAllOfferImages();
  const { menuEditorImageGalleryAddToOffersPage } = useListingManagerRoutes();

  useEffect(() => {
    imageGridRef.current?.scrollTo({ top: 0 });
  }, [activeTabId]);

  const { updateImageGalleryForNewImages } = useUpdateListingManagerImageGallery();

  const updateImageGallery = async ({ publishedImagesUrls }: UploadResult) => {
    setNewLoadingImagesCount(publishedImagesUrls.length);
    await updateImageGalleryForNewImages();
    setNewLoadingImagesCount(0);
  };

  return (
    <Root ref={imageGridRef}>
      <ImageGalleryHeader hasCloseButton onClose={onHeaderExitClick}>
        {headerContent}
      </ImageGalleryHeader>
      <TabsHeader>
        <Tabs
          activeTabId={activeTabId}
          items={[
            {
              id: optionTabs.ALL_IMAGES,
              label: intl.formatMessage({ id: 'shared-menu.editor.image-gallery.browse-images.tabs.all-images' }),
            },
            {
              id: optionTabs.PHOTOSHOOT_IMAGES,
              label: intl.formatMessage({
                id: 'shared-menu.editor.image-gallery.browse-images.tabs.photoshoot-images',
              }),
            },
          ]}
          onChange={(id: OptionTab) => setActiveTabId(id)}
        />
      </TabsHeader>
      <Content>
        <TabsContent>
          {Boolean(error) && <ListingManagerErrorPage error={error} />}
          {isSuccess && (
            <TabsContentTopBar>
              <Search />
              {hasAddToOfferAction && imageAssets.length > 0 && (
                <Button
                  variant="secondary"
                  size="tiny"
                  onClick={() => history.push(menuEditorImageGalleryAddToOffersPage())}
                >
                  <FormattedMessage id="shared-menu.editor.image-gallery.browse-images.add-to-offers" />
                </Button>
              )}
            </TabsContentTopBar>
          )}
          {isSuccess && activeTabId === optionTabs.ALL_IMAGES && (
            <ImagesUploader onUploadFinished={updateImageGallery} />
          )}
          {isSuccess && imageAssets.length === 0 && (
            <CenteredEmpty
              description={
                <FormattedMessage id="shared-menu.editor.image-gallery.empty-state.change-filters-upload-images" />
              }
              title={intl.formatMessage({ id: 'shared-menu.editor.image-gallery.empty-state.no-images-found' })}
            />
          )}
          {isSuccess && imageAssets.length > 0 && (
            <ImageGridLoader
              fetchNextPage={fetchNextPage}
              isLoading={isLoading || isFetchingNextPage}
              hasNextPage={hasNextPage}
              pageSize={PAGE_SIZE}
            >
              {Array.from({ length: newLoadingImagesCount }).map((_, index) => (
                <ImageCardSkeleton key={index} />
              ))}
              {imageAssets.map(asset => (
                <ImageCard
                  key={asset.id}
                  onClick={() => onImageCardClick(asset)}
                  enhanced={Boolean(asset.originalAssetId)}
                  attachedToOffer={allOfferImagesSet.has(IMAGE_BASE_URL + asset.id)}
                  blurhash={asset.blurhash}
                  name={asset.name}
                  src={IMAGE_BASE_URL + asset.id}
                  selected={selectedImagesIds.includes(asset.id)}
                  selectable
                />
              ))}
            </ImageGridLoader>
          )}
        </TabsContent>
      </Content>
    </Root>
  );
};
